import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useIsMobile, ExpandPill } from "@/components/MobileCollapse";
import { useLang } from "@/context/LanguageContext";
import { imgSrc } from "@/lib/api";

const IMG = {
  a: "/api/files/bertagnolli-site/uploads/57aae9c2-ed1f-4a78-874c-6ae1a7948c59.jpg",
  b: "/api/files/bertagnolli-site/uploads/5b45a3ca-8547-46f8-ade6-50d6c5908195.jpg",
  c: "/api/files/bertagnolli-site/uploads/05b3763e-31f3-46ba-bb75-cfec8a51237b.jpg",
  d: "/api/files/bertagnolli-site/uploads/00460371-cf63-40d1-8ac5-1c46db99d2a8.jpg",
  e: "/api/files/bertagnolli-site/uploads/dae11511-9d2a-4bb1-9b95-2965f431e1c2.jpg",
  f: "/api/files/bertagnolli-site/uploads/446f08d2-1f51-442d-95ae-7a4f92a9b04a.jpg",
  wc2015: "/api/files/bertagnolli-site/uploads/2aa59462-d4c5-4e2c-84c6-40378c2542a2.jpg",
  y2013: "/api/files/bertagnolli-site/uploads/4cf0ee67-4cf0-4ec5-be79-8fc016b3ca58.jpg",
  aspen2016: "/api/files/bertagnolli-site/uploads/af5b505c-6ac4-4f0b-9940-ebd81a68003c.jpg",
  tarvisio2017: "/api/files/bertagnolli-site/uploads/068160f6-896b-4414-8290-5ff6d6d3b6db.jpg",
};

const MILESTONES = [
  { year: "2026", type: "paralimpiadi", img: IMG.a, pos: "50% 32%" },
  { year: "2025", type: "mondiali", img: IMG.b, pos: "50% 40%" },
  { year: "2023", type: "mondiali", img: IMG.c, pos: "50% 40%" },
  { year: "2022", type: "paralimpiadi", img: IMG.d, pos: "50% 45%" },
  { year: "2019", type: "mondiali", img: IMG.e },
  { year: "2018", type: "paralimpiadi", img: IMG.f, pos: "50% 38%" },
  { year: "2017", type: "mondiali", img: IMG.tarvisio2017, pos: "50% 35%" },
  { year: "2016", type: "coppa_mondo", img: IMG.aspen2016, pos: "50% 40%" },
  { year: "2015", type: "coppa_mondo", img: IMG.wc2015, pos: "50% 30%" },
  { year: "2013", type: "altro", img: IMG.y2013, pos: "50% 30%" },
];

export default function Percorso() {
  const { t } = useLang();
  const items = t("percorso.items");
  const mobile = useIsMobile();
  const [peek, setPeek] = useState(false);
  const visibleYears = !mobile || peek ? MILESTONES : MILESTONES.slice(0, 2);
  return (
    <section id="percorso" className="relative py-12 sm:py-24 bg-[#0B101B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("percorso.eyebrow")}</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50">
            {t("percorso.titleA")}<span className="text-amber-400">{t("percorso.titleAccent")}</span>
          </h2>
          <p className="mt-3 text-sm text-slate-400">
            {t("percorso.subtitle")}
          </p>
        </Reveal>

        <div className="relative mt-10 sm:mt-12">
            <span className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-amber-400/70 via-slate-700 to-transparent md:-translate-x-1/2" />

          <div className="space-y-4 md:space-y-5">
            {visibleYears.map((m, i) => {
              const txt = items[i] || {};
              const left = i % 2 === 0;
              const link = `/risultati?type=${m.type}&anno=${m.year}`;
              return (
                <div key={m.year} className="relative md:grid md:grid-cols-2 md:gap-10 lg:gap-14 items-center">
                  <span className="absolute left-4 md:left-1/2 top-4 h-3 w-3 -translate-x-1/2 rotate-45 bg-amber-400 shadow-[0_0_14px_rgba(245,158,11,0.7)] z-10" />

                  <Reveal
                    x={left ? -40 : 40}
                    y={0}
                    className={`pl-11 md:pl-0 ${left ? "md:col-start-1 md:row-start-1 md:text-right" : "md:col-start-2 md:row-start-1"}`}
                  >
                    <Link
                      to={link}
                      data-testid={`timeline-card-${m.year}`}
                      className="group block rounded-xl border border-slate-800/80 bg-slate-900/60 backdrop-blur-md px-4 py-3.5 sm:px-5 hover:border-amber-400/50 transition-colors duration-300"
                    >
                      <span className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 ${left ? "md:justify-end" : ""}`}>
                        <span className="font-display text-2xl text-amber-400 leading-none">{m.year}</span>
                        <span className="text-sm sm:text-base font-bold text-slate-50 group-hover:text-amber-300 transition-colors duration-300">{txt.title}</span>
                        <span className="rounded-full border border-sky-400/40 px-2 py-0.5 text-[8px] uppercase tracking-[0.16em] text-sky-300">{txt.tag}</span>
                      </span>
                      <p className="mt-1 text-xs text-slate-400 leading-relaxed">{txt.desc}</p>
                      <span className={`mt-1.5 inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-amber-400/80 ${left ? "md:flex-row-reverse" : ""}`}>
                        {t("percorso.ctaYear")} {m.year}
                        <ArrowRight className={`h-3 w-3 transition-transform duration-300 group-hover:translate-x-1 ${left ? "md:-scale-x-100 md:group-hover:-translate-x-1 md:group-hover:translate-x-0" : ""}`} />
                      </span>
                      <span className="mt-3 block overflow-hidden rounded-lg border border-slate-800 md:hidden">
                        <img src={imgSrc(m.img, 640)} alt={txt.title} style={m.pos ? { objectPosition: m.pos } : undefined} className="w-full aspect-[16/9] object-cover" loading="lazy" />
                      </span>
                    </Link>
                  </Reveal>

                  <Reveal
                    x={left ? 40 : -40}
                    y={0}
                    delay={0.1}
                    className={`hidden md:block ${left ? "md:col-start-2 md:row-start-1" : "md:col-start-1 md:row-start-1"}`}
                  >
                    <Link to={link} data-testid={`timeline-photo-${m.year}`} className="group block relative max-w-sm mx-auto" tabIndex={-1}>
                      <div className={`overflow-hidden rounded-xl border border-slate-800/80 shadow-[0_14px_35px_rgba(0,0,0,0.4)] ${left ? "rotate-1" : "-rotate-1"} transition-transform duration-500 group-hover:rotate-0`}>
                        <img
                          src={imgSrc(m.img, 640)}
                          alt={txt.title}
                          style={m.pos ? { objectPosition: m.pos } : undefined}
                          loading="lazy"
                          className="w-full aspect-[2/1] object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                        />
                      </div>
                      <span className="font-display absolute -bottom-2.5 left-4 bg-[#090D14] border border-amber-400/50 text-amber-400 px-2.5 py-0.5 text-sm tracking-[0.12em]">
                        {m.year}
                      </span>
                    </Link>
                  </Reveal>
                </div>
              );
            })}
          </div>
          </div>
        {mobile && MILESTONES.length > 2 && (
          <ExpandPill open={peek} onToggle={() => setPeek(!peek)} hiddenCount={MILESTONES.length - 2} testid="expand-percorso" />
        )}
      </div>
    </section>
  );
}
